import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data stays fresh for 2 minutes - reduces API calls during navigation and tab switches
      // Real-time updates still come via SignalR, so staleness is acceptable
      // Individual queries that need faster refresh (dashboard, notifications) override this
      staleTime: 2 * 60 * 1000,

      // Keep cache for 5 minutes for navigation performance
      gcTime: 5 * 60 * 1000,

      // Retry failed requests
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors (client errors)
        const axiosError = error as { response?: { status?: number } } | undefined;
        if (axiosError?.response?.status !== undefined &&
            axiosError.response.status >= 400 &&
            axiosError.response.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },

      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch on window focus for fresh data
      refetchOnWindowFocus: true,

      // Refetch on reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      // Don't retry mutations to avoid duplicate requests
      retry: 0,
    },
  },
});

// Query key factory for consistent key generation
export const queryKeys = {
  all: ["admin"] as const,
  orders: () => [...queryKeys.all, "orders"] as const,
  ordersList: (filters?: Record<string, unknown>) =>
    [...queryKeys.orders(), "list", filters] as const,
  orderDetail: (id: number) => [...queryKeys.orders(), "detail", id] as const,
  orderCounts: () => [...queryKeys.orders(), "counts"] as const,

  serviceRequests: () => [...queryKeys.all, "service-requests"] as const,
  serviceRequestsList: (filters?: Record<string, unknown>) =>
    [...queryKeys.serviceRequests(), "list", filters] as const,
  serviceRequestCounts: () =>
    [...queryKeys.serviceRequests(), "counts"] as const,

  lateCheckouts: () => [...queryKeys.all, "late-checkouts"] as const,
  lateCheckoutsList: (filters?: Record<string, unknown>) =>
    [...queryKeys.lateCheckouts(), "list", filters] as const,
  lateCheckoutCounts: () => [...queryKeys.lateCheckouts(), "counts"] as const,

  transferRequests: () => [...queryKeys.all, "transfer-requests"] as const,
  transferRequestsList: (filters?: Record<string, unknown>) =>
    [...queryKeys.transferRequests(), "list", filters] as const,
  transferRequestCounts: () =>
    [...queryKeys.transferRequests(), "counts"] as const,

  menu: () => [...queryKeys.all, "menu"] as const,
  menuCategories: () => [...queryKeys.menu(), "categories"] as const,
  menuItems: (categoryId?: number) =>
    [...queryKeys.menu(), "items", categoryId] as const,

  rooms: () => [...queryKeys.all, "rooms"] as const,
  roomsList: () => [...queryKeys.rooms(), "list"] as const,

  housekeeping: () => [...queryKeys.all, "housekeeping"] as const,
  housekeepingStatus: () => [...queryKeys.housekeeping(), "status"] as const,

  dashboard: () => [...queryKeys.all, "dashboard"] as const,
  dashboardStats: () => [...queryKeys.dashboard(), "stats"] as const,
  dashboardActivity: () => [...queryKeys.dashboard(), "activity"] as const,
  dashboardSummary: (activityCount?: number) =>
    [...queryKeys.dashboard(), "summary", activityCount] as const,

  activeQrCodes: () => [...queryKeys.all, "active-qr-codes"] as const,
  activeQrCodesList: (filters?: Record<string, unknown>) =>
    [...queryKeys.activeQrCodes(), "list", filters] as const,

  notifications: () => [...queryKeys.all, "notifications"] as const,
  notificationCounts: () => [...queryKeys.notifications(), "counts"] as const,

  checkins: () => [...queryKeys.all, "checkins"] as const,
  checkinsList: (filters?: Record<string, unknown>) =>
    [...queryKeys.checkins(), "list", filters] as const,

  frontDeskBoard: () => [...queryKeys.all, "front-desk-board"] as const,
  frontDeskBoardDate: (date: string) =>
    [...queryKeys.frontDeskBoard(), date] as const,

  queue: () => [...queryKeys.all, "queue"] as const,
  queueItems: () => [...queryKeys.queue(), "items"] as const,

  unifiedRequests: () => [...queryKeys.all, "unified-requests"] as const,
  unifiedRequestsList: (filters?: Record<string, unknown>) =>
    [...queryKeys.unifiedRequests(), "list", filters] as const,

  chat: () => [...queryKeys.all, "chat"] as const,
  chatUnreadCount: () => [...queryKeys.chat(), "unread-count"] as const,

  search: () => [...queryKeys.all, "search"] as const,
  searchResults: (query: string) =>
    [...queryKeys.search(), "results", query] as const,

  sentiment: () => [...queryKeys.all, "sentiment"] as const,
  sentimentSummary: (qrTokenId: string) =>
    [...queryKeys.sentiment(), "summary", qrTokenId] as const,

  bootstrap: (hotelId?: string) => [...queryKeys.all, "bootstrap", hotelId] as const,

  kitchenBoard: () => [...queryKeys.all, "kitchen-board"] as const,

  statistics: () => [...queryKeys.all, "statistics"] as const,
  statisticsDashboard: () =>
    [...queryKeys.statistics(), "dashboard"] as const,
  statisticsRevenue: (filter?: Record<string, string | number | undefined>) =>
    [...queryKeys.statistics(), "revenue", filter] as const,
  statisticsOccupancy: (filter?: Record<string, string | number | undefined>) =>
    [...queryKeys.statistics(), "occupancy", filter] as const,
  statisticsServices: (filter?: Record<string, string | number | undefined>) =>
    [...queryKeys.statistics(), "services", filter] as const,
  statisticsGuests: (filter?: Record<string, string | number | undefined>) =>
    [...queryKeys.statistics(), "guests", filter] as const,
  statisticsOperational: (filter?: Record<string, string | number | undefined>) =>
    [...queryKeys.statistics(), "operational", filter] as const,
  statisticsComparative: (period1: string, period2: string) =>
    [...queryKeys.statistics(), "comparative", period1, period2] as const,
  statisticsOrdersByDayOfWeek: () =>
    [...queryKeys.statistics(), "orders-by-day-of-week"] as const,
  statisticsServiceRequestsByDayOfWeek: () =>
    [...queryKeys.statistics(), "service-requests-by-day-of-week"] as const,
} as const;
